import {
  VegaChartDto,
  VegaChartShareActionsConfigDto,
} from '@asset-metrix/portal-gateway-client-js';
import { Factory } from 'fishery';
import { Spec } from 'vega';

import { VegaChartSignal } from '../components/vega-charts/vegaChartSignal';

const defaultVegaChartSpecification: Spec = {
  $schema: 'https://vega.github.io/schema/vega/v5.json',
  autosize: 'fit',
  data: [{ name: 'table' }],
  scales: [
    {
      name: 'xscale',
      type: 'band',
      domain: { data: 'table', field: 'category' },
      range: 'width',
      padding: 0.05,
      round: true,
    },
    {
      name: 'yscale',
      type: 'linear',
      domain: { data: 'table', field: 'amount' },
      nice: true,
      range: 'height',
    },
  ],
  axes: [
    { orient: 'bottom', scale: 'xscale' },
    { orient: 'left', scale: 'yscale' },
  ],
  marks: [
    {
      type: 'rect',
      from: { data: 'table' },
      encode: {
        update: {
          fill: { value: 'steelblue' },
          x: { scale: 'xscale', field: 'category' },
          width: { scale: 'xscale', band: 1 },
          y: { scale: 'yscale', field: 'amount' },
          y2: { scale: 'yscale', value: 0 },
        },
        hover: { fill: { value: 'red' } },
      },
    },
    {
      type: 'text',
      encode: {
        enter: {
          align: { value: 'center' },
          baseline: { value: 'bottom' },
          fill: { value: 'black' },
        },
        update: {
          x: { scale: 'xscale', signal: 'tooltip.category', band: 0.5 },
          y: { scale: 'yscale', signal: 'tooltip.amount', offset: -2 },
          text: { signal: 'tooltip.amount' },
          fillOpacity: [
            { test: 'isNaN(tooltip.amount)', value: 0 },
            { value: 1 },
          ],
        },
      },
    },
  ],
  // we should always have at least an empty array here, so that we can safely push in the factories
  signals: [
    {
      name: 'width',
      init: 'isFinite(containerSize()[0]) ? containerSize()[0] : 200',
      on: [
        {
          update: 'isFinite(containerSize()[0]) ? containerSize()[0] : 200',
          events: 'window:resize',
        },
      ],
    },
    {
      name: 'height',
      init: 'isFinite(containerSize()[1]) ? containerSize()[1] : 200',
      on: [
        {
          update: 'isFinite(containerSize()[1]) ? containerSize()[1] : 200',
          events: 'window:resize',
        },
      ],
    },
    {
      name: 'tooltip',
      value: {},
      on: [
        { events: 'rect:mouseover', update: 'datum' },
        { events: 'rect:mouseout', update: '{}' },
      ],
    },
  ],
};

type VegaSpecTransientParams = {
  withDataRecordHeightSignal?: boolean;
  extraData?: Spec['data'];
  extraSignals?: Spec['signals'];
};

export const vegaChartSpecFactory = Factory.define<
  Spec,
  VegaSpecTransientParams
>(
  ({
    transientParams: { withDataRecordHeightSignal, extraData, extraSignals },
  }) => {
    const spec = structuredClone(defaultVegaChartSpecification);

    if (withDataRecordHeightSignal) {
      spec.signals?.push({
        name: VegaChartSignal.dataRecordHeight,
        value: 20,
      });
    }

    if (extraData) {
      spec.data?.push(...extraData);
    }

    if (extraSignals) {
      spec.signals?.push(...extraSignals);
    }

    return spec;
  }
);

export const vegaChartShareActionsConfigDtoFactory =
  Factory.define<VegaChartShareActionsConfigDto>(() => {
    return {
      actionKey: 'downloadXlsx',
    };
  });

export const vegaChartDtoFactory = Factory.define<
  VegaChartDto,
  VegaSpecTransientParams
>(({ transientParams }) => {
  return {
    type: 'vegaChart',
    config: {
      hasDataTable: true,
      heightfactor: 0.5,
      shareActions: [],
      customFilter: [],
      vegaSpecification: JSON.stringify(
        vegaChartSpecFactory.transient(transientParams).build()
      ),
      vegaChartDataType: 'rowData',
    },
  };
});
